type CanonicalJsonPrimitive = string | number | boolean | null;

export function canonicalStringify(value: unknown): string {
  const encoded = encodeCanonicalValue(value, "$", false);
  if (encoded === undefined) {
    throw new Error("canonical value must be a defined JSON value");
  }
  return encoded;
}

export function canonicalDigest(value: unknown): string {
  const bytes = new TextEncoder().encode(canonicalStringify(value));
  let high = 0xcbf29ce4;
  let low = 0x84222325;
  for (const byte of bytes) {
    low = (low ^ byte) >>> 0;
    const lowProduct = low * 0x1b3;
    high =
      (high * 0x1b3 +
        Math.floor(lowProduct / 0x1_0000_0000) +
        ((low << 8) >>> 0)) >>>
      0;
    low = lowProduct >>> 0;
  }
  return `fnv1a64:${high.toString(16).padStart(8, "0")}${low
    .toString(16)
    .padStart(8, "0")}`;
}

function encodeCanonicalValue(
  value: unknown,
  path: string,
  arrayElement: boolean,
): string | undefined {
  if (value === null) return "null";

  switch (typeof value) {
    case "string":
    case "boolean":
      return JSON.stringify(value satisfies CanonicalJsonPrimitive);
    case "number":
      if (!Number.isFinite(value)) {
        throw new Error(`${path} must contain finite JSON numbers`);
      }
      return JSON.stringify(value);
    case "undefined":
      return arrayElement ? "null" : undefined;
    case "object":
      break;
    default:
      throw new Error(`${path} must contain only plain JSON values`);
  }

  if (Array.isArray(value)) {
    return `[${value
      .map((entry, index) =>
        encodeCanonicalValue(entry, `${path}[${index}]`, true),
      )
      .join(",")}]`;
  }

  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) {
    const toJSON = (value as { toJSON?: unknown }).toJSON;
    if (typeof toJSON === "function") {
      return encodeCanonicalValue(
        toJSON.call(value),
        `${path}.toJSON()`,
        arrayElement,
      );
    }
    throw new Error(`${path} must contain only plain JSON values`);
  }

  const record = value as Readonly<Record<string, unknown>>;
  const fields: string[] = [];
  for (const key of Object.keys(record).sort()) {
    const encoded = encodeCanonicalValue(record[key], `${path}.${key}`, false);
    if (encoded !== undefined) {
      fields.push(`${JSON.stringify(key)}:${encoded}`);
    }
  }
  return `{${fields.join(",")}}`;
}
